import { useState } from 'react'
import { type Employee, EmployeePicker } from '@/entities/employee'
import {
  type Excursion,
  useCreateExcursionMutation,
  useUpdateExcursionMutation,
} from '@/entities/excursion'
import { errorMessage, type Warning } from '@/shared/api'
import type { Interval } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input } from '@/shared/ui/field'
import { IntervalField } from '@/shared/ui/interval-field'
import { toast } from '@/shared/ui/toast'

export interface ExcursionDialogState {
  excursion: Excursion | null
  slot: Interval
  guideIds: string[]
}

interface ExcursionDialogProps {
  state: ExcursionDialogState | null
  employees: Employee[]
  onClose: () => void
}

export function ExcursionDialog({ state, employees, onClose }: ExcursionDialogProps) {
  const create = useCreateExcursionMutation()
  const update = useUpdateExcursionMutation()

  const excursion = state?.excursion ?? null
  const [title, setTitle] = useState(excursion?.title ?? '')
  const [meetingPoint, setMeetingPoint] = useState(excursion?.meetingPoint ?? '')
  const [capacity, setCapacity] = useState(excursion?.capacity?.toString() ?? '')
  const [slot, setSlot] = useState<Interval>(excursion?.slot ?? state?.slot ?? emptySlot())
  const [guideIds, setGuideIds] = useState<string[]>(excursion?.guideIds ?? state?.guideIds ?? [])
  const [key, setKey] = useState('')

  const nextKey = `${state?.excursion?.id ?? 'new'}:${state?.slot.startsAt.toISOString() ?? ''}`
  if (state && key !== nextKey) {
    setKey(nextKey)
    setTitle(excursion?.title ?? '')
    setMeetingPoint(excursion?.meetingPoint ?? '')
    setCapacity(excursion?.capacity?.toString() ?? '')
    setSlot(excursion?.slot ?? state.slot)
    setGuideIds(excursion?.guideIds ?? state.guideIds)
  }

  const report = (warnings: Warning[]) => {
    if (warnings.length > 0) {
      const names = [...new Set(warnings.map((w) => w.employeeId))]
        .map((id) => employees.find((e) => e.id === id)?.name ?? 'гид')
        .join(', ')
      toast.notify(`Гид занят: ${names}`)
    }
    onClose()
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const payload = {
      title: title.trim(),
      slot,
      meetingPoint: meetingPoint.trim() || null,
      guideIds,
      capacity: capacity.trim() === '' ? null : Number(capacity),
    }
    if (excursion) {
      update.mutate(
        { id: excursion.id, patch: payload },
        { onSuccess: (result) => report(result.warnings) },
      )
    } else {
      create.mutate(payload, { onSuccess: (result) => report(result.warnings) })
    }
  }

  const pending = create.isPending || update.isPending
  const error = create.error ?? update.error
  const locked = excursion !== null && excursion.state !== 'planned'

  return (
    <Dialog
      open={state !== null}
      onClose={onClose}
      title={excursion ? 'Выход' : 'Новый выход'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            type="submit"
            form="excursion-form"
            disabled={pending || locked || title.trim().length === 0}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <form id="excursion-form" onSubmit={submit} className="flex flex-col gap-5">
        {locked && (
          <p className="text-sm text-warning">
            Выход уже {excursion?.state === 'held' ? 'состоялся' : 'отменён'} — менять его нельзя.
          </p>
        )}

        <Field label="Название">
          {(props) => (
            <Input {...props} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          )}
        </Field>

        <IntervalField value={slot} onChange={setSlot} />

        <Field label="Точка сбора">
          {(props) => (
            <Input
              {...props}
              value={meetingPoint}
              placeholder="Например: у арки на Рубинштейна"
              onChange={(e) => setMeetingPoint(e.target.value)}
            />
          )}
        </Field>

        <EmployeePicker
          label="Кто ведёт"
          employees={employees}
          selected={guideIds}
          onChange={setGuideIds}
        />

        <Field
          label="Вместимость"
          hint="Мягкий ориентир: перебор допустим, это предупреждение. Пусто — без ограничения"
        >
          {(props) => (
            <Input
              {...props}
              type="number"
              min={1}
              value={capacity}
              placeholder="Без ограничения"
              onChange={(e) => setCapacity(e.target.value)}
            />
          )}
        </Field>

        {error && (
          <p role="alert" className="text-sm text-accent">
            {errorMessage(error)}
          </p>
        )}
      </form>
    </Dialog>
  )
}

function emptySlot(): Interval {
  const startsAt = new Date()
  startsAt.setMinutes(0, 0, 0)
  return { startsAt, endsAt: new Date(startsAt.getTime() + 2 * 3_600_000) }
}
